import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Badge,
} from 'reactstrap';
import notify, { MESSAGES } from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';
import ActionButton from '../../../Components/Common/ActionButton';
import { SkeletonChart } from '../../../Components/Common/Skeleton';

import { api } from '../../../config';
import { DEFAULT_RIGHTS } from '../constants';
import { buildAssignToastMessage, getMenuName } from '../utils/menuRoleUtils';
import DualListPanel from './DualListPanel';

const AssignMenuModal = ({
  isOpen,
  toggle,
  token,
  selectedRole,
  menuOptions,
}) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [originalAssignments, setOriginalAssignments] = useState([]);
  const [originalMenuIds, setOriginalMenuIds] = useState([]);
  const [assignedMenuIds, setAssignedMenuIds] = useState([]);
  const [leftSelection, setLeftSelection] = useState([]);
  const [rightSelection, setRightSelection] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [lastLeftIndex, setLastLeftIndex] = useState(null);
  const [lastRightIndex, setLastRightIndex] = useState(null);

  const resetState = () => {
    setAssignedMenuIds([]);
    setOriginalMenuIds([]);
    setOriginalAssignments([]);
    setLeftSelection([]);
    setRightSelection([]);
    setLeftSearch('');
    setRightSearch('');
    setHasChanges(false);
    setLastLeftIndex(null);
    setLastRightIndex(null);
  };

  useEffect(() => {
    if (!isOpen || !selectedRole || !token) return;

    const loadAssignments = async () => {
      resetState();
      setLoadingData(true);

      try {
        const response = await fetch(
          `${api.API_URL}/menu-role/by-role/${selectedRole.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to load assignment data');

        const result = await response.json();
        const rawData = result.data?.items ?? result.data;
        const existingItems = Array.isArray(rawData) ? rawData : [];

        setOriginalAssignments(existingItems);

        const existingMenuIds = existingItems
          .filter(item => item && item.menuId != null)
          .map(item => String(item.menuId));

        setOriginalMenuIds(existingMenuIds);
        setAssignedMenuIds(existingMenuIds);
      } catch (error) {
        notify.apiError(error, 'load assigned menus');
        toggle();
      } finally {
        setLoadingData(false);
      }
    };

    loadAssignments();
  }, [isOpen, selectedRole, token]);

  useEffect(() => {
    if (!isOpen) return;

    const currentSet = new Set(assignedMenuIds.map(String));
    const originalSet = new Set(originalMenuIds.map(String));

    const isDifferent =
      assignedMenuIds.length !== originalMenuIds.length ||
      assignedMenuIds.some(id => !originalSet.has(id)) ||
      originalMenuIds.some(id => !currentSet.has(id));

    setHasChanges(isDifferent);
  }, [assignedMenuIds, originalMenuIds, isOpen]);

  useEffect(() => {
    setLastLeftIndex(null);
  }, [leftSearch]);

  useEffect(() => {
    setLastRightIndex(null);
  }, [rightSearch]);

  const handleClose = () => {
    resetState();
    toggle();
  };

  // User-initiated dismiss (X, backdrop, Cancel) asks before dropping edits.
  const requestClose = async () => {
    if (hasChanges && !modalLoading) {
      const discard = await confirmAction({
        tone: 'warning',
        title: 'Discard changes?',
        message: 'Your menu assignment changes have not been saved.',
        subject: selectedRole?.name,
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!discard) return;
    }
    handleClose();
  };

  const assignedSet = new Set(assignedMenuIds.map(String));

  const availableMenus = menuOptions.filter(
    m =>
      !assignedSet.has(String(m.id)) &&
      (m.label || m.name || '').toLowerCase().includes(leftSearch.toLowerCase())
  );

  const selectedMenusList = menuOptions.filter(
    m =>
      assignedSet.has(String(m.id)) &&
      (m.label || m.name || '')
        .toLowerCase()
        .includes(rightSearch.toLowerCase())
  );

  const moveRight = () => {
    if (leftSelection.length === 0) return;
    setAssignedMenuIds(prev => [...prev, ...leftSelection.map(String)]);
    setLeftSelection([]);
  };

  const moveAllRight = () => {
    const allAvailable = menuOptions
      .filter(m => !assignedSet.has(String(m.id)))
      .map(m => String(m.id));
    setAssignedMenuIds(prev => [...prev, ...allAvailable]);
    setLeftSelection([]);
  };

  const moveLeft = () => {
    if (rightSelection.length === 0) return;
    const removeIds = new Set(rightSelection.map(String));
    setAssignedMenuIds(prev => prev.filter(id => !removeIds.has(String(id))));
    setRightSelection([]);
  };

  const moveAllLeft = () => {
    setAssignedMenuIds([]);
    setRightSelection([]);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      notify.info(MESSAGES.noChanges);
      handleClose();
      return;
    }

    const removedCount = originalAssignments.filter(
      o => !assignedMenuIds.map(String).includes(String(o.menuId))
    ).length;
    if (removedCount > 0) {
      const confirmed = await confirmAction({
        tone: 'danger',
        title: `Remove ${removedCount} ${removedCount === 1 ? 'menu' : 'menus'} from this role?`,
        message:
          'Users with this role lose access to the removed menus. You can assign them again later.',
        subject: selectedRole?.name,
        confirmLabel: `Remove ${removedCount === 1 ? 'menu' : 'menus'}`,
      });
      if (!confirmed) return;
    }

    setModalLoading(true);
    try {
      const currentSet = new Set(assignedMenuIds);
      const originalSet = new Set(originalMenuIds);

      const menusToAdd = assignedMenuIds
        .filter(id => !originalSet.has(String(id)))
        .map(String);

      const menusToRemove = originalAssignments.filter(
        o => !currentSet.has(String(o.menuId))
      );

      const removePromises = menusToRemove.map(mapping =>
        fetch(`${api.API_URL}/menu-role/${mapping.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to remove menu ${getMenuName(menuOptions, mapping.menuId)}`
              );
            }
            return { menuId: mapping.menuId, mappingId: mapping.id };
          })
          .catch(err => {
            throw { menuId: mapping.menuId, message: err.message };
          })
      );

      const addPromises = menusToAdd.map(menuId =>
        fetch(`${api.API_URL}/menu-role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            roleId: selectedRole.id,
            menuId,
            ...DEFAULT_RIGHTS,
          }),
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to assign menu ${getMenuName(menuOptions, menuId)}`
              );
            }
            return { menuId };
          })
          .catch(err => {
            throw { menuId, message: err.message };
          })
      );

      const [removeResults, addResults] = await Promise.all([
        Promise.allSettled(removePromises),
        Promise.allSettled(addPromises),
      ]);

      const enrichedRemove = removeResults.map((res, idx) => ({
        ...res,
        menuId: menusToRemove[idx]?.menuId,
      }));

      const enrichedAdd = addResults.map((res, idx) => ({
        ...res,
        menuId: menusToAdd[idx],
      }));

      const totalFailed =
        enrichedRemove.filter(r => r.status === 'rejected').length +
        enrichedAdd.filter(r => r.status === 'rejected').length;

      const message = buildAssignToastMessage(
        menuOptions,
        enrichedAdd,
        enrichedRemove
      );

      if (totalFailed === 0) {
        notify.success(message);
        handleClose();
      } else {
        notify.warning(message);

        const successRemoves = enrichedRemove
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.mappingId);

        const remainingAssignments = originalAssignments.filter(
          o => !successRemoves.includes(o.id)
        );

        const successAdds = enrichedAdd
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.menuId);

        const newAssignedIds = [
          ...remainingAssignments.map(o => o.menuId),
          ...successAdds,
        ];

        setOriginalAssignments(
          originalAssignments.filter(o => newAssignedIds.includes(o.menuId))
        );
        setOriginalMenuIds(newAssignedIds.map(String));
        setAssignedMenuIds(newAssignedIds.map(String));
        setHasChanges(false);
      }
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={requestClose}
      size='xl'
      backdrop='static'
      centered
    >
      <ModalHeader toggle={requestClose} className='bg-light'>
        Assign Menus to {selectedRole?.name || selectedRole?.id}
      </ModalHeader>
      <ModalBody>
        <Row className='g-3'>
          <Col md={12}>
            <Label>Menu Assignment</Label>
            {loadingData ? (
              <Row className='align-items-center'>
                <Col xs={12} md={5}>
                  <SkeletonChart height={240} />
                </Col>
                <Col xs={12} md={2} />
                <Col xs={12} md={5}>
                  <SkeletonChart height={240} />
                </Col>
              </Row>
            ) : (
              <Row className='align-items-center'>
                <Col xs={12}>
                  <div className='d-flex align-items-center flex-wrap gap-2 text-muted fs-12 mb-1'>
                    <i className='ri-information-line text-info fs-14'></i>
                    <span>Click</span> <span className='fw-medium'>select</span>
                    <span className='mx-1'>•</span>
                    <Badge color='light' className='text-dark border fs-10'>
                      Ctrl / ⌘
                    </Badge>
                    <span>toggle</span>
                    <span className='mx-1'>•</span>
                    <Badge color='light' className='text-dark border fs-10'>
                      Shift
                    </Badge>
                    <span>range select</span>
                  </div>
                </Col>
                <Col xs={12} md={5} className='order-1 order-md-1'>
                  <DualListPanel
                    title='Available Menus'
                    items={availableMenus}
                    selectedIds={leftSelection}
                    searchValue={leftSearch}
                    onSearchChange={setLeftSearch}
                    onSelectionChange={setLeftSelection}
                    lastIndex={lastLeftIndex}
                    onLastIndexChange={setLastLeftIndex}
                    emptyMessage='No available menus'
                  />
                </Col>
                <Col
                  xs={12}
                  md={2}
                  className='order-2 order-md-2 d-flex flex-row flex-md-column justify-content-center align-items-center gap-2 my-2 my-md-0'
                >
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-arrow-right-double-line'
                    label='Move All to Selected'
                    onClick={moveAllRight}
                    disabled={
                      menuOptions.filter(m => !assignedSet.has(String(m.id)))
                        .length === 0
                    }
                  />
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-arrow-right-s-line'
                    label='Move Selected to the Right'
                    onClick={moveRight}
                    disabled={leftSelection.length === 0}
                  />
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-arrow-left-s-line'
                    label='Move Selected to the Left'
                    onClick={moveLeft}
                    disabled={rightSelection.length === 0}
                  />
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-arrow-left-double-line'
                    label='Remove All from Selected'
                    onClick={moveAllLeft}
                    disabled={assignedMenuIds.length === 0}
                  />
                </Col>
                <Col xs={12} md={5} className='order-3 order-md-3'>
                  <DualListPanel
                    title='Selected Menus'
                    titleClassName='border rounded p-2 bg-primary text-white text-center mb-2 fw-medium fs-12'
                    items={selectedMenusList}
                    selectedIds={rightSelection}
                    searchValue={rightSearch}
                    onSearchChange={setRightSearch}
                    onSelectionChange={setRightSelection}
                    lastIndex={lastRightIndex}
                    onLastIndexChange={setLastRightIndex}
                    emptyMessage='No selected menus'
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={requestClose}
          disabled={modalLoading}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-save-line'
          onClick={handleSave}
          loading={modalLoading}
          disabled={!hasChanges}
        >
          Save Assignments
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default AssignMenuModal;
